import React, { useContext, useMemo } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Table, Heading } from '@frontend/design-system';
import { InsuranceReportContext } from '../dental-insurance-eligibility-report/onederful-insurance-eligibility-report/context/OnederfulReportContext.provider';
import { filterTableData } from '../helpers/get-extras.helper';
import { GridTableEnum } from '../types/tables-data-type';
import { whiteRoundedBg } from './common-styles';
import { GridContainerContext } from './grid-container';
import { SeeMoreOrLessButton } from './see-more-or-less-button';
import { Summary } from './summary';

type copaymentsProps = {
  style?: SerializedStyles;
  isPrintingReport?: boolean;
};

export const CopaymentsTable = React.memo(({ style, isPrintingReport = false }: copaymentsProps) => {
  const { t } = useTranslation('insuranceEligibilityReport');
  const {
    coPaymentTableData,
    copaymentsTableColConfigs,
    shouldShowCoPayment,
    showNetworkFilter,
    filters,
    copaymentSummary,
    selectedNetworkFilterValue,
  } = useContext(InsuranceReportContext);
  const { selected, setSelected, selectedStyle, notSelectedStyle } = useContext(GridContainerContext);

  const isSelected = selected === GridTableEnum.COPAYMENT;
  const isNotSelected = !!selected && selected !== GridTableEnum.COPAYMENT;

  const tableData = useMemo(
    () => filterTableData({ data: coPaymentTableData, filters, showNetworkFilter }),
    [coPaymentTableData, filters, showNetworkFilter]
  );

  if (shouldShowCoPayment) {
    return (
      <motion.div
        layout
        css={[containerStyle, whiteRoundedBg, isSelected && selectedStyle, isNotSelected && notSelectedStyle, style]}
      >
        <motion.div layout='position' css={headingContainerStyle}>
          <Heading level={2}>{t('Copayment')}</Heading>
          <SeeMoreOrLessButton
            onClick={() => setSelected(isSelected ? '' : GridTableEnum.COPAYMENT)}
            isSelected={isSelected}
          />
        </motion.div>
        <Summary
          applyFilters={!isPrintingReport}
          data={copaymentSummary ?? []}
          expanded={isSelected}
          noDataFoundMessage={t('No data found for {{selectedNetworkFilterValue}}', { selectedNetworkFilterValue })}
        />
        {(isPrintingReport || isSelected) && (
          <Table
            colConfig={copaymentsTableColConfigs}
            data={tableData}
            hasResponsiveColWidths
            isFocusable
            isSelectable={false}
            wrapperStyle={wrapperStyle}
            isPaginated={!isPrintingReport}
          />
        )}
      </motion.div>
    );
  }
  return null;
});

CopaymentsTable.displayName = 'CopaymentsTable';

const containerStyle = css`
  grid-column: span 3;
  @media (max-width: 900px) {
    grid-column: 1/3;
  }
`;

const headingContainerStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const wrapperStyle = css`
  border-radius: ${theme.borderRadius.small};
  overflow: hidden;
  background-color: white;
  padding: ${theme.spacing(1, 0, 0, 0)};
`;
